import {
  CreateDeliveryNoteLineDto,
  CustomerDeliveryNoteService,
  DocLineRole,
} from '@digiwall/goliath-documents/proxy';
import { Component, EventEmitter, inject, Input, model, OnInit, Output } from '@angular/core';
import { GoliathContactsEnumerationTypeStore } from '@digiwall/goliath-contacts';
import { ToasterService } from '@abp/ng.theme.shared';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DeliveryNoteCreationGridComponent } from './delivery-note-creation-grid/delivery-note-creation-grid.component';
import { DeliveryNotePackagingGridComponent } from './delivery-note-packaging-grid/delivery-note-packaging-grid.component';
import { LocalizationService } from '@abp/ng.core';
import { DeliveryNoteDepositGridComponent } from './delivery-note-deposit-grid/delivery-note-deposit-grid.component';
import { InvoicingConfigurationComponent } from './invoicing-configuration/invoicing-configuration.component';
import { DocRole } from '@digiwall/goliath-core/proxy';
import { OdysseyOffcanvas } from '@digiwall/core';

@Component({
  selector: 'document-delivery-note-creation-dialog',
  templateUrl: './delivery-note-creation-dialog.component.html',
  imports: [
    DialogBoxComponent,
    DeliveryNoteCreationGridComponent,
    DeliveryNoteDepositGridComponent,
    DeliveryNotePackagingGridComponent,
    InvoicingConfigurationComponent,
  ],
})
export class DeliveryNoteCreationDialogComponent implements OnInit {
  enumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  offCanvaService = inject(OdysseyOffcanvas);

  @Input() salesPurchaseId: string;
  allowPartialDelivery = model<boolean>(false);

  @Input() currencyPrecision: number = 2;
  @Output() docCreated = new EventEmitter<{
    docId: string;
    docRole: DocRole;
    textTemplateId: string | null;
  }>();

  private get salesPurchaseTotalAmount(): number {
    return this.lines
      .filter(x => x.lineRole === DocLineRole.SalesPurchaseArticle)
      .reduce((acc, line) => {
        const amount = this.round(
          line.unitPrice * line.remainingQuantityToDeliver -
            line.unitPrice * line.remainingQuantityToDeliver * (line.discount / 100),
        );

        return parseFloat((acc + amount).toFixed(this.currencyPrecision));
      }, 0);
  }
  private get factor() {
    return Math.pow(10, this.currencyPrecision);
  }

  protected lines: CreateDeliveryNoteLineDto[];
  protected packagings: { articleId: string; quantity: number; description: string }[] = [];

  protected generateInvoice = false;
  protected invoicingDaybookId: string = null;
  protected invoicingPrintTemplateId: string = null;
  protected sendInvoice = false;
  protected invoiceEmailTemplateId: string = null;

  constructor(
    private deliveryNoteService: CustomerDeliveryNoteService,
    private toasterService: ToasterService,
    private localizationService: LocalizationService,
  ) {}

  ngOnInit(): void {
    this.deliveryNoteService.getLinesForDelivery(this.salesPurchaseId).subscribeOnce(lines => {
      this.lines = lines.map(line => {
        line.selectedQuantityToDeliver = line.remainingQuantityToDeliver;
        return line;
      });
    });
  }

  save() {
    const validationError = this.validateBeforeSave();
    if (validationError) {
      this.toasterService.error(validationError);
      return;
    }

    this.deliveryNoteService
      .createFromCustomerSalesPurchase(this.salesPurchaseId, {
        linesForDelivery: this.lines.filter(x => x.selectedQuantityToDeliver != 0),
        packagings: this.packagings,
        generateInvoice: this.generateInvoice,
        invoicingDaybookId: this.invoicingDaybookId,
        invoicingPrintTemplateId: this.invoicingPrintTemplateId,
      })
      .subscribeOnce(response => {
        if (response) {
          this.docCreated.emit({
            docId: response.result,
            docRole: this.generateInvoice ? DocRole.CustomerInvoice : DocRole.CustomerDeliveryNote,
            textTemplateId: this.invoiceEmailTemplateId,
          });
          this.offCanvaService.dismiss();
        }
      });
  }

  private validateBeforeSave(): string | null {
    const totalSelected = this.getTotalSelected();

    const validationRules: { ruleFailed: boolean; errorMessage: string }[] = [
      {
        ruleFailed: this.packagings.length <= 0 && totalSelected < 0,
        errorMessage: 'Doc::DeliveryNote:NegativeTotalPrice',
      },
      {
        ruleFailed: totalSelected < 0,
        errorMessage: this.localizationService.instant(
          'Doc::DeliveryNote:TotalSelectedLessThanDeposit',
          totalSelected.toString(),
        ),
      },
      {
        ruleFailed:
          this.articleLines.some(
            line => line.remainingQuantityToDeliver != line.selectedQuantityToDeliver,
          ) &&
          totalSelected > this.salesPurchaseTotalAmount &&
          this.articleLines.some(x => x.isDeposit),
        errorMessage: this.localizationService.instant(
          'Doc::DeliveryNote:TotalSelectedGreaterThanRemaining',
          totalSelected.toString(),
          this.salesPurchaseTotalAmount.toString(),
          this.totalDepositAmount().toString(),
        ),
      },
    ];

    const failedValidation = validationRules.find(v => v.ruleFailed);
    return failedValidation ? failedValidation.errorMessage : null;
  }

  private getTotalSelected(): number {
    return this.round(
      this.articleLines
        .filter(x => x.selectedQuantityToDeliver != null)
        .reduce(
          (total, line: any) =>
            total + (line.isDeposit ? (line.amountToDeliver ?? 0) * -1 : (line.totalPrice ?? 0)),
          0,
        ),
    );
  }

  private totalDepositAmount() {
    return this.round(
      this.articleLines
        .filter(x => x.isDeposit)
        .reduce((totalDeposit, line) => (totalDeposit += line.unitPrice), 0),
    );
  }

  private round(amount: number) {
    const roundedAmount =
      this.factor != 0 ? Math.round(amount * this.factor) / this.factor : Math.round(amount);
    return roundedAmount;
  }

  private get articleLines() {
    return this.lines.filter(line => line.lineRole === DocLineRole.SalesPurchaseArticle);
  }
}
